import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';

import { IQ_SEVERITY_DISPLAYS } from 'src/app/shared/constants/iq-observation-action-labels';
import { Contact } from 'src/app/models/contact.model';
import { canUseSampleData } from 'src/app/shared/utils/sample-data-toggle.util';
import * as fromStore from '../../../../store';
// eslint-disable-next-line import/no-cycle
import { SynapseService } from 'src/app/core/services/synapse';
import {
  AccountDeviceData,
  DeviceEvent,
  UnusualNetworkMetrics,
} from '../../domain/unusual-network-metrics.model';

const CC_METRICS = Object.freeze({
  SPECIALS: ['critical', 'warning'],
  CATEGORIES_MAP: {
    investigate: 'critical',
    monitor: 'warning',
    observe: 'other',
    trivial: 'other',
  },
  METRIC_MAP: {
    bandwidth: 'dataUtilization',
    packetloss: 'packetLost',
    latency: 'latency',
    jitter: 'jitter',
  },
});

@Component({
  selector: 'app-unm-page',
  templateUrl: './unm-page.component.html',
  standalone: false,
})
export class UnmPageComponent implements OnDestroy, OnInit {
  measurementCategory: string = 'warning';

  dataUtilization: string = '0';

  latency: string = '0';

  jitter: string = '0';

  packetLost: string = '0';

  accountName: string = '';

  nitelIQData: any[] = [];

  selectedTime: string = '';

  showMockData: boolean = false;

  canShowSampleDataToggle = signal(false);

  isNoContent: boolean = false;

  isLoading: boolean = false;

  severityBreakdownData: number[] = [];

  severityBreakdownLabels: string[] = [
    ...IQ_SEVERITY_DISPLAYS,
  ];

  severityBreakdownTitle: string = 'Actionable Insights';

  timeOfDayBreakdownData: number[] = [];

  timeOfDayBreakdownLabels: string[] = ['12a - 8a', '8a - 4p', '4p - 12a'];

  timeOfDayBreakdownTitle: string = 'Time of Day Breakdown';

  topUnusualDevicesData: { name: string; data: number[] }[] = [];

  topUnusualDevicesCategories: string[] = [];

  topUnusualDevicesTitle: string = 'Top Unusual Devices';

  topUnusualDevicesYtitle: string = 'Device Lead Count';

  unmTableData = new MatTableDataSource<UnusualNetworkMetrics>();

  // Raw data from the API
  unmRawDeviceEvents$: Observable<AccountDeviceData> = null;

  private lastDeviceEvents: DeviceEvent[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private synapseService: SynapseService,
    private cdr: ChangeDetectorRef,
    private store: Store<fromStore.State>,
  ) {
    this.selectedTime = '1d';
  }

  ngOnInit(): void {
    // A single, persistent subscription so this page reloads whenever the
    // account genuinely changes (e.g. via the account switcher) even while
    // already mounted - route guards only re-run on navigation.
    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        filter(Boolean),
        distinctUntilChanged(
          (prev, curr) =>
            (prev?.Account?.Name ?? null) === (curr?.Account?.Name ?? null),
        ),
        tap((data: Contact) => {
          this.accountName = data.Account?.Name;
          this.canShowSampleDataToggle.set(canUseSampleData(data));
        }),
        catchError((err) => {
          console.error('Error fetching user data:', err);
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.loadIQData());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Handle time selection change from a child component
  onChildSelectionChange(value: string): void {
    this.selectedTime = value;
    this.getUserInfoAndLoadData();
  }

  // Toggles between mock and real data
  toggleMockData(event: boolean): void {
    this.showMockData = event;
    this.getUserInfoAndLoadData(); // Reload data
  }

  // Retrieves user information and loads unusual network data
  getUserInfoAndLoadData(): void {
    this.loadIQData(); // Refresh data using the already-known account
  }

  loadIQData(): void {
    this.unmTableData = new MatTableDataSource<UnusualNetworkMetrics>();
    this.isLoading = true;
    this.isNoContent = false;
    this.measurementCategory = 'warning';
    this.nitelIQData = [];

    this.synapseService
      .fetchAllUnusualMetrics(
        this.selectedTime,
        this.showMockData,
        this.accountName,
      )
      .pipe(
        tap((data) => {
          this.nitelIQData = data;
          this.isNoContent = !data?.length;
          if (data?.length) {
            const validCategories = this.getValidCategories(data);
            if (!validCategories.includes(this.measurementCategory)) {
              this.measurementCategory = validCategories[0] || 'warning';
            }

            this.updateMetrics();
          }
        }),
        catchError((err) => {
          console.error('Error fetching metrics:', err);
          this.isNoContent = true;
          return of([]); // fallback to empty array if needed
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.loadDeviceEvents();

    this.cdr.detectChanges();
  }

  getValidCategories(data: { bucket: string }[]): string[] {
    const categories = new Set<string>();
    data.forEach((item) => {
      const bucket = item.bucket.toLowerCase().trim();
      categories.add(bucket);
    });
    return Array.from(categories);
  }

  // Update metrics, charts, and table data
  private updateMetrics(): void {
    this.calculateCategoryMetrics(this.measurementCategory);
    this.calculateSeverityBreakdown();
    this.calculateTimeOfDayBreakdown();
    this.updateTopUnusualDevicesFromEvents();
    this.populateUnmTableData();
  }

  loadDeviceEvents(): void {
    this.unmRawDeviceEvents$ = this.synapseService.getUnmDeviceEvents(
      this.accountName,
      this.selectedTime,
      this.showMockData,
    );

    this.unmRawDeviceEvents$.subscribe((data) => {
      this.lastDeviceEvents = data?.devices || [];
      this.updateTopUnusualDevicesFromEvents();
    });
  }

  // Calculate metrics (e.g., utilization, latency) based on the selected category
  calculateCategoryMetrics(category: string): void {
    category = CC_METRICS.CATEGORIES_MAP[category.toLowerCase()] ?? category;
    category = category.toLowerCase().trim(); // Normalize category name

    /** Initial running totals */
    const totals = this.nitelIQData.reduce(
      (acc, { bucket, metrics }) => {
        const b = bucket.toLowerCase().trim();

        const belongs = !CC_METRICS.SPECIALS.includes(category)
          ? !CC_METRICS.SPECIALS.includes(b)
          : b === category;

        if (!belongs) return acc;

        for (const { metric_type, metric_value } of metrics) {
          const key = CC_METRICS.METRIC_MAP[metric_type.toLowerCase().trim()];
          if (key) acc[key] += parseFloat(metric_value);
        }

        acc.count += 1;
        return acc;
      },
      {
        dataUtilization: 0,
        packetLost: 0,
        latency: 0,
        jitter: 0,
        count: 0,
      },
    );

    const { dataUtilization, latency, jitter, packetLost, count } = totals;
    this.dataUtilization = (dataUtilization / count || 0).toFixed(2);
    this.packetLost = (packetLost / count || 0).toFixed(2);
    this.latency = (latency / count || 0).toFixed(2);
    this.jitter = (jitter / count || 0).toFixed(2);
    this.measurementCategory = category;
  }

  // Calculate the breakdown of severity (e.g., warning, critical, other)
  calculateSeverityBreakdown(): void {
    const buckets = { warning: 0, critical: 0, other: 0 };
    this.nitelIQData.forEach((item) => {
      const bucket = item.bucket.toLowerCase().trim() as string; // Normalize bucket name
      if (bucket === 'warning') {
        buckets.warning += 1;
      } else if (bucket === 'critical') {
        buckets.critical += 1;
      } else {
        buckets.other += 1;
      }
    });
    this.severityBreakdownData = [
      buckets.critical,
      buckets.warning,
      buckets.other,
    ]; // Update chart data
  }

  // Calculate time-of-day breakdown for metrics
  calculateTimeOfDayBreakdown(): void {
    const timeBuckets = [0, 0, 0]; // Initialize time buckets
    this.nitelIQData.forEach((item) => {
      const hours = new Date(item.time_frame).getUTCHours(); // Extract hours
      if (hours < 8) timeBuckets[0] += 1;
      else if (hours < 16) timeBuckets[1] += 1;
      else timeBuckets[2] += 1;
    });
    this.timeOfDayBreakdownData = timeBuckets; // Update chart data
  }

  updateTopUnusualDevicesFromEvents(): void {
    const deviceMap = new Map<string, number>();

    this.lastDeviceEvents.forEach((event) => {
      deviceMap.set(
        event.device_name,
        (deviceMap.get(event.device_name) || 0) + event.lead_count,
      );
    });

    const sortedDevices = Array.from(deviceMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    this.topUnusualDevicesData = [
      {
        name: 'Device Lead Count',
        data: sortedDevices.map(([_, count]) => count),
      },
    ];
    this.topUnusualDevicesCategories = sortedDevices.map(([name]) => name);
  }

  // Populate table data with network metrics
  populateUnmTableData(): void {
    const tableData = this.nitelIQData.map((item) => {
      // Convert metrics array to key-value pairs
      const metrics = item.metrics.reduce(
        (acc: any, metric: any) => ({
          ...acc,
          [metric.metric_type]: metric.metric_value,
        }),
        {},
      );
      // Construct table row
      return {
        edgeName: item.device_name,
        interfaceName: item.source,
        timestamp: this.formatDate(item.time_frame), // Format timestamp
        bucket: item.bucket,
        ...metrics, // Spread metrics into row
      };
    });
    this.unmTableData.data = tableData; // Update table data source
  }

  // Format a timestamp into a readable string
  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('en-US')} ${date.toLocaleTimeString(
      'en-US',
      {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      },
    )}`;
  }

  get hasBarAndTableContent(): boolean {
    const barHasData =
      Array.isArray(this.topUnusualDevicesData?.[0]?.data) &&
      this.topUnusualDevicesData[0].data.length > 0;
    const tableHasData =
      Array.isArray(this.lastDeviceEvents) && this.lastDeviceEvents.length > 0;
    return barHasData || tableHasData;
  }
}
